import React, { useState, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { Project } from '../data/projects'
import { Text } from './Text'
import { Icon } from './Icon'
import {
  SiReact,
  SiTypescript,
  SiJavascript,
  SiNodedotjs,
  SiMongodb,
  SiGraphql,
  SiAmazon,
  SiDocker,
  SiGit,
  SiStyledcomponents,
  SiI18Next,
  SiFramer,
  SiPaypal,
  SiGooglesheets,
  SiGmail,
  SiReactrouter,
  SiRedux,
  SiMobx,
  SiSoundcloud,
  SiReactos,
  SiHermes,
  SiMetabase,
  SiSage,
  SiWebrtc,
  SiThealgorithms,
  SiShell,
  SiHtml5,
  SiNetflix,
  SiWellsfargo,
  SiZillow,
  SiFacebook,
  SiYoutube,
  SiInstagram,
} from 'react-icons/si'
import { FaKeyboard } from 'react-icons/fa'
import { Image } from './Image'

// Base styled components
const Card = styled(motion.div)<{ $variant: 'minimal' | 'detailed' | 'page' }>`
  background: ${(props) =>
    props.$variant === 'minimal' ? '#181A1B' : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 16px;
  transition: all 0.3s ease;
  display: flex;
  border: ${(props) =>
    props.$variant === 'minimal' ? '1px solid #4ECDC4' : 'none'};
  flex-direction: ${(props) => (props.$variant === 'page' ? 'row' : 'column')};
  height: 100%;
  min-width: ${(props) => (props.$variant === 'minimal' ? '280px' : 'auto')};
  max-width: ${(props) =>
    props.$variant === 'minimal'
      ? '350px'
      : props.$variant === 'page'
      ? '1200px'
      : 'none'};
  padding: ${(props) =>
    props.$variant === 'minimal'
      ? '1.5rem'
      : props.$variant === 'page'
      ? '0'
      : '1.5rem'};
  cursor: ${(props) => (props.$variant === 'minimal' ? 'pointer' : 'default')};
  margin: ${(props) => (props.$variant === 'page' ? '0 auto' : '0')};
  position: relative;
  scroll-padding-top: 2rem;
  height: ${(props) => (props.$variant === 'minimal' ? '350px' : 'auto')};

  @media (max-width: 1024px) {
    flex-direction: column;
    max-width: ${(props) =>
      props.$variant === 'minimal'
        ? '350px'
        : props.$variant === 'page'
        ? '700px'
        : 'none'};
  }

  @media (max-width: 768px) {
    max-width: ${(props) =>
      props.$variant === 'minimal'
        ? '350px'
        : props.$variant === 'page'
        ? '500px'
        : 'none'};
  }

  @media (max-width: 480px) {
    max-width: ${(props) =>
      props.$variant === 'minimal'
        ? '350px'
        : props.$variant === 'page'
        ? '100%'
        : 'none'};
    border-radius: ${(props) => (props.$variant === 'page' ? '0' : '16px')};
  }

  &:hover {
    transform: ${(props) =>
      props.$variant === 'minimal' ? 'translateY(-4px)' : 'none'};
    background: ${(props) =>
      props.$variant === 'minimal'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(255, 255, 255, 0.05)'};
    box-shadow: ${(props) =>
      props.$variant === 'minimal' ? '0 4px 20px rgba(0, 0, 0, 0.1)' : 'none'};
  }
`

const StageIndicator = styled.div<{
  $variant: 'minimal' | 'detailed' | 'page'
}>`
  display: ${(props) => (props.$variant === 'page' ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.03);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  min-width: 200px;
  justify-content: center;

  @media (max-width: 1024px) {
    display: flex;
    flex-direction: row;
    padding: 1rem 2rem;
    min-width: unset;
    width: 100%;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    justify-content: flex-start;
    gap: 1rem;
    align-items: center;
  }

  @media (max-width: 768px) {
    padding: 0.8rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.7rem 1.2rem;
  }
`

const MainContent = styled.div<{ $variant: 'minimal' | 'detailed' | 'page' }>`
  flex: 1;
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    width: 100%;
  }
`

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  scroll-margin-top: 2rem;

  @media (max-width: 1024px) {
    width: 100%;
  }
`

const CardHeader = styled.div<{ $variant: 'minimal' | 'detailed' | 'page' }>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: ${(props) => (props.$variant === 'page' ? '3rem 3rem 2rem' : '0')};
  background: ${(props) =>
    props.$variant === 'page' ? 'rgba(255, 255, 255, 0.03)' : 'transparent'};
  border-bottom: ${(props) =>
    props.$variant === 'page' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'};
  position: relative;
  scroll-margin-top: 2rem;

  @media (max-width: 1024px) {
    padding: ${(props) =>
      props.$variant === 'page' ? '2rem 2rem 1.5rem' : '0'};
  }

  @media (max-width: 768px) {
    padding: ${(props) =>
      props.$variant === 'page' ? '1.5rem 1.5rem 1rem' : '0'};
  }
`

const StatusBadge = styled.div<{
  $status: string
  $variant: 'minimal' | 'detailed' | 'page'
}>`
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  display: inline-block;
  background: ${(props) =>
    props.$status === 'Featured'
      ? 'rgba(78, 205, 196, 0.1)'
      : props.$status === 'In Development'
      ? 'rgba(255, 171, 0, 0.1)'
      : 'rgba(255, 107, 107, 0.1)'};
  color: ${(props) =>
    props.$status === 'Featured'
      ? '#4ECDC4'
      : props.$status === 'In Development'
      ? '#FFAB00'
      : '#ff6b6b'};
  position: ${(props) => (props.$variant === 'page' ? 'static' : 'static')};
  transform: ${(props) =>
    props.$variant === 'page' ? 'rotate(-90deg)' : 'none'};
  white-space: nowrap;
  margin: ${(props) =>
    props.$variant === 'minimal' ? '0 0 0.75rem 0' : '1rem 0'};

  @media (max-width: 1024px) {
    position: static;
    transform: none;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`

const CardContent = styled.div<{ $variant: 'minimal' | 'detailed' | 'page' }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${(props) => (props.$variant === 'page' ? '0 3rem' : '0')};
  gap: ${(props) => (props.$variant === 'minimal' ? '1rem' : '2rem')};
  height: ${(props) => (props.$variant === 'minimal' ? '100%' : 'auto')};

  @media (max-width: 1024px) {
    padding: ${(props) => (props.$variant === 'page' ? '0 2rem' : '0')};
    gap: ${(props) =>
      props.$variant === 'page'
        ? '1.5rem'
        : props.$variant === 'minimal'
        ? '1rem'
        : '1rem'};
  }

  @media (max-width: 768px) {
    padding: ${(props) => (props.$variant === 'page' ? '0 1.5rem' : '0')};
    gap: ${(props) =>
      props.$variant === 'page'
        ? '1.25rem'
        : props.$variant === 'minimal'
        ? '1rem'
        : '1rem'};
  }
`

const MediaContainer = styled.div<{
  $variant: 'minimal' | 'detailed' | 'page'
}>`
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    45deg,
    rgba(78, 205, 196, 0.1),
    rgba(255, 107, 107, 0.1)
  );
  border-radius: ${(props) => (props.$variant === 'page' ? '12px' : '8px')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4ecdc4;
  font-size: 1.2rem;
  font-weight: 500;
  transition: all 0.3s ease;
  margin: ${(props) => (props.$variant === 'page' ? '2rem 0' : '0')};
  box-shadow: ${(props) =>
    props.$variant === 'page' ? '0 4px 20px rgba(0, 0, 0, 0.1)' : 'none'};

  ${(props) =>
    props.$variant === 'page'
      ? `
    width: 100%;
    height: 400px;

    @media (max-width: 1024px) {
      height: 300px;
      margin: 1.5rem 0;
    }

    @media (max-width: 768px) {
      height: 250px;
      margin: 1.25rem 0;
    }

    @media (max-width: 480px) {
      height: 200px;
      margin: 1rem 0;
    }
  `
      : props.$variant === 'detailed'
      ? `
  @media (min-width: 1024px) {
    width: 350px;
    min-width: 350px;
    height: 250px;
  }
  @media (max-width: 1023px) {
    width: 100%;
    height: 200px;
    margin-bottom: 1rem;
  }
  `
      : 'display: none;'}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      rgba(78, 205, 196, 0.05),
      rgba(255, 107, 107, 0.05)
    );
    z-index: 1;
  }

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    position: relative;
    z-index: 2;
  }

  span {
    position: relative;
    z-index: 2;
    text-align: center;
    padding: 1rem;
    font-size: 1.2rem;
  }
`

const Description = styled(Text)<{ $variant: 'minimal' | 'detailed' | 'page' }>`
  display: ${(props) =>
    props.$variant === 'minimal' ? '-webkit-box' : 'block'};
  -webkit-line-clamp: ${(props) =>
    props.$variant === 'minimal' ? '1' : 'none'};
  -webkit-box-orient: ${(props) =>
    props.$variant === 'minimal' ? 'vertical' : 'unset'};
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: ${(props) => (props.$variant === 'minimal' ? '1.4' : '1.8')};
  color: #a0a0a0;
  font-size: ${(props) =>
    props.$variant === 'minimal' ? '0.95rem' : '1.1rem'};
  max-width: ${(props) => (props.$variant === 'page' ? '800px' : 'none')};
  margin: ${(props) => (props.$variant === 'minimal' ? '0 0 0.5rem 0' : '0')};
  letter-spacing: 0.3px;
  text-align: ${(props) => (props.$variant === 'page' ? 'justify' : 'left')};
  padding: ${(props) => (props.$variant === 'page' ? '0 1rem' : '0')};

  @media (max-width: 768px) {
    font-size: ${(props) =>
      props.$variant === 'minimal' ? '0.95rem' : '1rem'};
    text-align: left;
    padding: 0;
  }
`

const TechStack = styled.div<{ $variant: 'minimal' | 'detailed' | 'page' }>`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: ${(props) => (props.$variant === 'minimal' ? 'auto' : '0.5rem')};
  padding: ${(props) => (props.$variant === 'page' ? '1.5rem 0' : '0')};
  border-top: ${(props) =>
    props.$variant === 'page' ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'};
  border-bottom: ${(props) =>
    props.$variant === 'page' ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'};

  @media (max-width: 480px) {
    gap: 0.4rem;
  }
`

const TechTag = styled.span`
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 500;
  background: rgba(78, 205, 196, 0.1);
  color: #4ecdc4;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.25rem 0.7rem;
    font-size: 0.8rem;
  }
`

const MoreTechIndicator = styled(TechTag)`
  background: rgba(78, 205, 196, 0.1);
  color: #4ecdc4;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.85rem;
  padding: 0.25rem 0.7rem;

  &::before {
    content: '+';
    font-weight: bold;
  }

  @media (max-width: 480px) {
    padding: 0.2rem 0.6rem;
    font-size: 0.8rem;
  }
`

const CardFooter = styled.div<{ $variant: 'minimal' | 'detailed' | 'page' }>`
  padding: ${(props) => (props.$variant === 'page' ? '2rem 3rem' : '0')};
  background: ${(props) =>
    props.$variant === 'page' ? 'rgba(255, 255, 255, 0.03)' : 'transparent'};
  border-top: ${(props) =>
    props.$variant === 'page' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'};
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: ${(props) => (props.$variant === 'minimal' ? '1rem' : '1rem')};

  @media (max-width: 1024px) {
    padding: ${(props) => (props.$variant === 'page' ? '1.5rem 2rem' : '0')};
  }

  @media (max-width: 768px) {
    padding: ${(props) => (props.$variant === 'page' ? '1.25rem 1.5rem' : '0')};
    flex-direction: ${(props) =>
      props.$variant === 'page' ? 'column' : 'row'};
  }
`

const ActionButton = styled.button<{
  $variant?: 'primary' | 'secondary'
  $isPage?: boolean
}>`
  padding: ${(props) => (props.$isPage ? '0.9rem 1.8rem' : '0.8rem 1.5rem')};
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: ${(props) =>
    props.$variant === 'primary'
      ? 'linear-gradient(45deg, #4ecdc4, #ff6b6b)'
      : 'transparent'};
  border: ${(props) =>
    props.$variant === 'secondary' ? '2px solid #4ecdc4' : 'none'};
  color: ${(props) => (props.$variant === 'secondary' ? '#4ecdc4' : 'white')};
  flex: ${(props) => (props.$isPage ? '1' : 'none')};
  min-width: ${(props) => (props.$isPage ? '200px' : 'auto')};
  width: ${(props) => (props.$isPage ? 'auto' : '100%')};
  text-align: center;
  font-size: ${(props) => (props.$isPage ? '1rem' : '0.95rem')};
  letter-spacing: 0.5px;

  @media (max-width: 480px) {
    padding: ${(props) => (props.$isPage ? '0.8rem 1.5rem' : '0.7rem 1.2rem')};
    font-size: ${(props) => (props.$isPage ? '0.95rem' : '0.9rem')};
  }

  &:hover {
    transform: translateY(-2px);
    background: ${(props) =>
      props.$variant === 'secondary'
        ? '#4ecdc4'
        : 'linear-gradient(45deg, #ff6b6b, #4ecdc4)'};
    color: ${(props) => (props.$variant === 'secondary' ? '#0a0a0a' : 'white')};
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
`

// Update tech icon mapping with available icons
const techIconMap: Record<string, any> = {
  React: SiReact,
  TypeScript: SiTypescript,
  JavaScript: SiJavascript,
  HTML5: SiHtml5,
  'Node.js': SiNodedotjs,
  MongoDB: SiMongodb,
  GraphQL: SiGraphql,
  AWS: SiAmazon,
  Docker: SiDocker,
  Git: SiGit,
  'Styled-Components': SiStyledcomponents,
  i18next: SiI18Next,
  'Framer Motion': SiFramer,
  'PayPal API': SiPaypal,
  'Google Sheets API': SiGooglesheets,
  'JWT Authentication': SiShell,
  LocalStorage: SiSage,
  EmailJS: SiGmail,
  'React Router': SiReactrouter,
  Redux: SiRedux,
  'MobX-State-Tree': SiMobx,
  'use-sound': SiSoundcloud,
  'RTL Support': SiWebrtc,
  'Theme System': SiThealgorithms,
  'Error Handling': SiHermes,
  SessionStorage: SiMetabase,
  'React Icons': SiReactos,
  SiInstagram,
}

// Update color map for technology icons
const techIconColorMap: Record<string, string> = {
  javascript: '#f7df1e',
  typescript: '#3178c6',
  react: '#61dafb',
  html5: '#e34f26',
  'node.js': '#3c873a',
  mongodb: '#47a248',
  aws: '#ff9900',
  git: '#f34f29',
  docker: '#2496ed',
  graphql: '#e535ab',
  'styled-components': '#db7093',
  i18next: '#26a69a',
  'framer motion': '#0055ff',
  'paypal api': '#003087',
  'google sheets api': '#0f9d58',
  'jwt authentication': '#000000',
  localstorage: '#f7df1e',
  emailjs: '#ea4335',
  'react router': '#ca4245',
  redux: '#764abc',
  'mobx-state-tree': '#ff9955',
  'use-sound': '#ff5500',
  'rtl support': '#4ecdc4',
  'theme system': '#4ecdc4',
  'error handling': '#ff6b6b',
  sessionstorage: '#f7df1e',
  'react icons': '#61dafb',
}

// Map project IDs to relevant icons
const projectIconMap: Record<string, any> = {
  'wf-clone': SiWellsfargo,
  zillow: SiZillow,
  'amazon-clone': SiAmazon,
  'facebook-clone': SiFacebook,
  'netflix-clone': SiNetflix,
  'youtube-clone': SiYoutube,
  metatypingclub: FaKeyboard,
  'instagram-clone': SiInstagram,
}

// Add a styled wrapper for icon margin
const ProjectIconWrapper = styled.span`
  margin-right: 0.1rem;
`

// Add a color map for project icons
const projectIconColorMap: Record<string, string> = {
  'wf-clone': '#c40404', // Wells Fargo red
  zillow: '#0074e4', // Zillow blue
  'amazon-clone': '#ff9900', // Amazon orange
  'facebook-clone': '#1877f2', // Facebook blue
  'netflix-clone': '#e50914', // Netflix red
  'youtube-clone': '#ff0000', // YouTube red
  metatypingclub: '#4ecdc4', // MetaTypingClub teal
  'instagram-clone': '#e1306c', // Instagram pink
}

const ExpandButton = styled(motion.button)`
  background: rgba(78, 205, 196, 0.1);
  border: none;
  color: #4ecdc4;
  cursor: pointer;
  padding: 0.8rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  border-radius: 25px;
  transition: all 0.3s ease;
  width: fit-content;

  &:hover {
    background: rgba(78, 205, 196, 0.2);
    color: #ff6b6b;
    transform: translateY(-2px);
  }

  span {
    transition: transform 0.3s ease;
    font-size: 0.8rem;
  }

  &[aria-expanded='true'] span {
    transform: rotate(180deg);
  }
`

const GradientOverlay = styled.div<{ $isExpanded: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(transparent, rgba(10, 10, 10, 0.95));
  pointer-events: none;
  opacity: ${(props) => (props.$isExpanded ? 0 : 1)};
  transition: opacity 0.3s ease;
`

// Add new styled components for description sections
const DescriptionSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 1.5rem;
`

const DescriptionBlock = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.08);
  }
`

const DescriptionTitle = styled(Text)`
  font-size: 1.3rem;
  font-weight: 600;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  position: relative;
  padding-left: 1.75rem;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 10px;
    background: linear-gradient(45deg, #4ecdc4, #ff6b6b);
    border-radius: 50%;
  }
`

const DescriptionContent = styled.div`
  color: #b0b0b0;
  font-size: 1.1rem;
  line-height: 1.8;
  margin: 0;
  padding-left: 1.75rem;
  text-align: left;
  letter-spacing: 0.3px;
  position: relative;

  p {
    margin: 0 0 1rem 0;
    white-space: pre-wrap;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding-left: 1.5rem;
  }
`

const DescriptionList = styled(motion.ul)`
  list-style: none;
  padding: 0;
  margin: 0;
  padding-left: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const DescriptionListItem = styled(motion.li)`
  color: #b0b0b0;
  font-size: 1.1rem;
  line-height: 1.6;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  position: relative;
  padding-left: 1.25rem;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.7rem;
    width: 6px;
    height: 6px;
    background: #4ecdc4;
    border-radius: 50%;
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`

// Animation variants
const expandVariants = {
  expanded: {
    height: 'auto',
    opacity: 1,
    y: 0,
    transition: {
      height: {
        duration: 0.5,
        ease: [0.04, 0.62, 0.23, 0.98],
      },
      opacity: {
        duration: 0.3,
        delay: 0.1,
      },
      y: {
        duration: 0.4,
        ease: [0.04, 0.62, 0.23, 0.98],
      },
    },
  },
  collapsed: {
    height: 'var(--collapsed-height)',
    opacity: 1,
    y: 0,
    transition: {
      height: {
        duration: 0.4,
        ease: [0.04, 0.62, 0.23, 0.98],
      },
      opacity: {
        duration: 0.2,
      },
      y: {
        duration: 0.3,
        ease: [0.04, 0.62, 0.23, 0.98],
      },
    },
  },
}

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.3,
      ease: 'easeOut',
    },
  }),
}

const DescriptionPreview = styled(motion.div)`
  position: relative;
  overflow: hidden;
`

const DescriptionFull = styled(motion.div)`
  position: relative;
`

const SectionLabel = styled(Text)`
  font-size: 1.2rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  padding-left: 1.75rem;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 10px;
    background: linear-gradient(45deg, #4ecdc4, #ff6b6b);
    border-radius: 50%;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 1.25rem;
  }
`

// Add styled-components for the overview and details sections
const OverviewSection = styled.section`
  margin-bottom: 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
`
const OverviewTitle = styled.div`
  font-weight: 600;
  color: #fff;
  font-size: 1.15rem;
  margin-bottom: 8px;
`
const OverviewText = styled.div`
  color: #b0b0b0;
  font-size: 1.1rem;
  margin-bottom: 12px;
`
const DetailsSection = styled.section`
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 1.5rem;
`
const DetailsTitle = styled.div`
  font-weight: 600;
  color: #fff;
  font-size: 1.15rem;
  margin-bottom: 8px;
`

type ProjectCardVariant = 'minimal' | 'detailed' | 'page'

interface ProjectCardProps {
  project: Project
  variant: ProjectCardVariant
  onClick?: () => void
  onViewDemo?: () => void
  onViewSource?: () => void
  ref?: React.Ref<HTMLDivElement>
}

export const ProjectCard = React.forwardRef<HTMLDivElement, ProjectCardProps>(
  ({ project, variant, onClick, onViewDemo, onViewSource }, ref) => {
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)
    const [isTechStackExpanded, setIsTechStackExpanded] = useState(false)

    const handleClick = useCallback(() => {
      if (variant === 'minimal' && onClick) {
        setTimeout(() => {
          window.location.hash = `project-${project.id}`
        }, 100)
        onClick()
      }
    }, [variant, onClick, project.id])

    const getYouTubeEmbedUrl = useCallback((url: string) => {
      const regExp =
        /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
      const match = url.match(regExp)
      if (match && match[2].length === 11) {
        return `https://www.youtube.com/embed/${match[2]}?modestbranding=1&rel=0&showinfo=0&playsinline=1&enablejsapi=1`
      }
      return null
    }, [])

    const renderMedia = useCallback(() => {
      if (project.videoUrl) {
        const embedUrl = getYouTubeEmbedUrl(project.videoUrl)
        if (embedUrl) {
          return (
            <iframe
              src={embedUrl}
              title={`${project.title} video`}
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              allowFullScreen
              loading='lazy'
              style={{ pointerEvents: 'auto' }}
              onClick={(e) => {
                e.stopPropagation()
              }}
            />
          )
        }
      }
      if (project.image) {
        return <Image src={project.image} alt={project.title} />
      }
      return <span>{project.title}</span>
    }, [project.videoUrl, project.image, project.title, getYouTubeEmbedUrl])

    const getFirstThreeSentences = useCallback((text: string): string => {
      const sentences = text.split(/[.!?]+/).filter(Boolean)
      return (
        sentences.slice(0, 3).join('. ') + (sentences.length > 3 ? '...' : '')
      )
    }, [])

    const parseDescription = useCallback(
      (description: string, isPreview: boolean) => {
        // Split into sections first
        const sections = description.split('\n\n').filter(Boolean)

        return sections.map((section, index) => {
          const lines = section.split('\n')
          const title = lines[0]
          const content = lines.slice(1).join('\n').trim()

          // Check if content contains bullet points
          const hasBulletPoints = content
            .split('\n')
            .some((line) => line.trim().startsWith('•'))

          if (hasBulletPoints) {
            const items = content
              .split('\n')
              .filter((line) => line.trim().startsWith('•'))
              .map((item) => item.replace('•', '').trim())

            const visibleItems = isPreview ? items.slice(0, 3) : items

            return {
              type: 'list' as const,
              title,
              items: visibleItems,
              totalItems: items.length,
            }
          }

          // Regular text section
          const paragraphs = content.split(/\n\n+/).filter(Boolean)
          return {
            type: 'text' as const,
            title,
            content: isPreview ? getFirstThreeSentences(content) : paragraphs,
          }
        })
      },
      [getFirstThreeSentences]
    )

    const renderDescriptionContent = useCallback(
      (isPreview: boolean) => {
        const parsedContent = parseDescription(
          project.fullDescription,
          isPreview
        )

        return (
          <DescriptionSection>
            {parsedContent.map((section, index) => {
              if (section.type === 'list') {
                return (
                  <DescriptionBlock
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}>
                    <DescriptionTitle text={section.title || 'Key Features'} />
                    <DescriptionList>
                      {section.items.map((item, itemIndex) => (
                        <DescriptionListItem
                          key={itemIndex}
                          custom={itemIndex}
                          variants={listItemVariants}
                          initial='hidden'
                          animate={
                            isDescriptionExpanded ? 'visible' : 'hidden'
                          }>
                          {item}
                        </DescriptionListItem>
                      ))}
                      {isPreview && section.totalItems > 3 && (
                        <DescriptionListItem
                          custom={3}
                          variants={listItemVariants}
                          initial='hidden'
                          animate='visible'>
                          ...and {section.totalItems - 3} more features
                        </DescriptionListItem>
                      )}
                    </DescriptionList>
                  </DescriptionBlock>
                )
              }

              return (
                <DescriptionBlock
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}>
                  <DescriptionTitle text={section.title} />
                  <DescriptionContent>
                    {isPreview ? (
                      <p>{section.content as string}</p>
                    ) : (
                      (section.content as string[]).map(
                        (paragraph: string, pIndex: number) => (
                          <p key={pIndex}>{paragraph}</p>
                        )
                      )
                    )}
                  </DescriptionContent>
                </DescriptionBlock>
              )
            })}
          </DescriptionSection>
        )
      },
      [project.fullDescription, isDescriptionExpanded, parseDescription]
    )

    const renderMinimalDescription = useCallback(() => {
      // Truncate to 130 characters
      const desc = project.shortDescription.replace(/\s+/g, ' ').trim()
      const truncatedText =
        desc.length > 50 ? desc.slice(0, 50).trim() + '...' : desc

      return (
        <Description
          text={truncatedText}
          fontSize='1rem'
          margin='0'
          $variant='minimal'
        />
      )
    }, [project.shortDescription])

    const descriptionContent = useMemo(() => {
      if (variant !== 'page') {
        // ... existing code for minimal/detailed ...
        return null
      }
      return (
        <>
          <OverviewSection>
            <OverviewTitle>Project Overview</OverviewTitle>
            <OverviewText>{project.shortDescription}</OverviewText>
            {!isDescriptionExpanded && (
              <ExpandButton
                onClick={() => setIsDescriptionExpanded(true)}
                aria-expanded={isDescriptionExpanded}
                aria-label='Read more'
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}>
                Read more
                <motion.span
                  animate={{ rotate: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ display: 'inline-block', marginLeft: 6 }}>
                  ▼
                </motion.span>
              </ExpandButton>
            )}
          </OverviewSection>
          {/* Full Description (visible after Read more) */}
          {isDescriptionExpanded && (
            <DetailsSection>
              <DetailsTitle>Key Features & Details</DetailsTitle>
              {renderDescriptionContent(false)}
              <ExpandButton
                onClick={() => setIsDescriptionExpanded(false)}
                aria-expanded={isDescriptionExpanded}
                aria-label='Close full description'
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{ marginTop: 16 }}>
                Close
                <motion.span
                  animate={{ rotate: 180 }}
                  transition={{ duration: 0.3 }}
                  style={{ display: 'inline-block', marginLeft: 6 }}>
                  ▼
                </motion.span>
              </ExpandButton>
            </DetailsSection>
          )}
        </>
      )
    }, [
      variant,
      isDescriptionExpanded,
      renderDescriptionContent,
      project.shortDescription,
    ])

    const renderTechStack = () => {
      // Calculate visible technologies and remaining count
      const isMinimal = variant === 'minimal'
      const visibleTechs = isMinimal
        ? project.technologies.slice(0, 3)
        : isTechStackExpanded
        ? project.technologies
        : project.technologies.slice(0, 7)

      return (
        <>
          {variant === 'page' && <SectionLabel text='Technologies Used' />}
          <TechStack $variant={variant}>
            {visibleTechs.map((tech, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}>
                <TechTag>
                  {techIconMap[tech] && (
                    <motion.div
                      initial={{ rotate: -180, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      transition={{ delay: i * 0.05 + 0.1 }}>
                      {React.createElement(techIconMap[tech], {
                        color:
                          techIconColorMap[tech.toLowerCase()] || '#4ecdc4',
                      })}
                    </motion.div>
                  )}
                  {tech}
                </TechTag>
              </motion.div>
            ))}
            {/* Only show expand button for detailed and page variants, and place it after the last tech */}
            {!isMinimal && project.technologies.length > 7 && (
              <ExpandButton
                onClick={() => setIsTechStackExpanded(!isTechStackExpanded)}
                aria-expanded={isTechStackExpanded}
                aria-label={
                  isTechStackExpanded
                    ? 'Show less'
                    : `Show all (${project.technologies.length})`
                }
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}>
                {isTechStackExpanded
                  ? 'Show less'
                  : `Show All ${project.technologies.length}`}
                <motion.span
                  animate={{ rotate: isTechStackExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}>
                  ▼
                </motion.span>
              </ExpandButton>
            )}
          </TechStack>
        </>
      )
    }

    const renderDescription = useCallback(() => {
      if (variant === 'minimal') {
        return renderMinimalDescription()
      }
      return descriptionContent
    }, [variant, renderMinimalDescription, descriptionContent])

    return (
      <Card
        ref={ref}
        $variant={variant}
        role='article'
        onClick={handleClick}
        id={project.id}>
        {variant === 'page' ? (
          <>
            <StageIndicator $variant={variant}>
              <StatusBadge $status={project.status} $variant={variant}>
                {project.status}
              </StatusBadge>
            </StageIndicator>
            <MainContent $variant={variant}>
              <CardHeader $variant={variant}>
                {projectIconMap[project.id] && (
                  <ProjectIconWrapper>
                    <Icon
                      icon={projectIconMap[project.id]}
                      size={45}
                      color={projectIconColorMap[project.id] || '#4ecdc4'}
                    />
                  </ProjectIconWrapper>
                )}
                <HeaderContent>
                  <Text
                    text={project.title}
                    fontSize='2rem'
                    fontWeight='bold'
                    color='#ffffff'
                    margin='0'
                    role='heading'
                    aria-level={2}
                  />
                </HeaderContent>
              </CardHeader>

              <MediaContainer $variant={variant}>
                {renderMedia()}
              </MediaContainer>

              <CardContent $variant={variant}>
                {renderDescription()}
                {renderTechStack()}

                <CardFooter $variant={variant}>
                  {project.link && project.link !== '#' && (
                    <ActionButton
                      $variant='primary'
                      $isPage={true}
                      onClick={onViewDemo}
                      role='button'
                      aria-label={`View ${project.title} demo`}>
                      View Live Demo
                      <span>→</span>
                    </ActionButton>
                  )}
                  {project.github && (
                    <ActionButton
                      $variant='secondary'
                      $isPage={true}
                      onClick={onViewSource}
                      role='button'
                      aria-label={`View ${project.title} source code`}>
                      View Source Code
                      <span>→</span>
                    </ActionButton>
                  )}
                </CardFooter>
              </CardContent>
            </MainContent>
          </>
        ) : (
          <>
            {variant === 'detailed' && (
              <MediaContainer $variant={variant}>
                {renderMedia()}
              </MediaContainer>
            )}

            <CardContent $variant={variant}>
              <StatusBadge $status={project.status} $variant={variant}>
                {project.status}
              </StatusBadge>

              {variant === 'minimal' && (
                <CardHeader $variant={variant}>
                  {projectIconMap[project.id] && (
                    <ProjectIconWrapper>
                      <Icon
                        icon={projectIconMap[project.id]}
                        size={28}
                        color={projectIconColorMap[project.id] || '#4ecdc4'}
                      />
                    </ProjectIconWrapper>
                  )}
                  <HeaderContent>
                    <Text
                      text={project.title}
                      fontSize='1.5rem'
                      fontWeight='bold'
                      color='#ffffff'
                      margin='0'
                    />
                  </HeaderContent>
                </CardHeader>
              )}

              {variant === 'detailed' && (
                <CardHeader $variant={variant}>
                  {projectIconMap[project.id] && (
                    <ProjectIconWrapper>
                      <Icon
                        icon={projectIconMap[project.id]}
                        size={32}
                        color={projectIconColorMap[project.id] || '#4ecdc4'}
                      />
                    </ProjectIconWrapper>
                  )}
                  <HeaderContent>
                    <Text
                      text={project.title}
                      fontSize='1.5rem'
                      fontWeight='bold'
                      color='#ffffff'
                      margin='0'
                      role='heading'
                      aria-level={3}
                    />
                  </HeaderContent>
                </CardHeader>
              )}

              {renderDescription()}
              {renderTechStack()}

              {/* <CardFooter $variant={variant}> */}
              {variant === 'minimal' && (
                <ActionButton
                  $variant='secondary'
                  style={{ cursor: 'pointer' }}>
                  More Info →
                </ActionButton>
              )}
              {/* </CardFooter> */}
            </CardContent>
          </>
        )}
      </Card>
    )
  }
)
